import * as React from 'react';
import { isEmpty, pick, throttle } from 'lodash';

import { FormField } from 'components/form';
import InputArray from 'components/inputs/input-array/InputArray';
import Input from 'components/inputs/input/Input';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import Tooltip from 'components/Tooltip';

import DeliverableTag from 'modules/deliverables/components/deliverable-tag/DeliverableTag';
import DeliverableLandingPageFields from '../deliverable-landing-page-fields/DeliverableLandingPageFields';

import { __t, applyTranslations, t } from 'utils/language';
import { maxLength } from 'utils/validators';
import { copyTextToClipboard, isDefined } from 'utils/helper/common';

import type { Deliverable, DeliverableDetailsFormTemplate, DeliverableTag as DeliverableTagAlias, TextEntry } from 'types';

import type { SyntheticEvent } from 'react';

applyTranslations(require('./deliverable-details-form.i18n.json'));

import './deliverable-details-edit.scss';

export const MAX_ITEMS = 30;
const MAX_LOCATION_LENGTH = 1024;

const INSTAGRAM_POST_TEMPLATE: DeliverableDetailsFormTemplate = [
  {
    name: 'hashtags',
    label: __t('deliverable_details_form.hashtags_label'),
    tooltipMessage: __t('deliverable_details_form.hashtags_tooltip'),
    prefix: '#',
    max: MAX_ITEMS,
    inputType: 'array'
  },
  {
    name: 'tags',
    label: __t('deliverable_details_form.tags_label'),
    tooltipMessage: __t('deliverable_details_form.tags_tooltip_instagram'),
    prefix: '@',
    max: MAX_ITEMS,
    inputType: 'array'
  },
  {
    name: 'mentions',
    label: __t('deliverable_details_form.mentions_label'),
    tooltipMessage: __t('deliverable_details_form.mentions_tooltip'),
    prefix: '@',
    max: MAX_ITEMS,
    inputType: 'array'
  },
  {
    name: 'location',
    label: __t('deliverable_details_form.location_label'),
    tooltipMessage: __t('deliverable_details_form.location_tooltip'),
    max: MAX_LOCATION_LENGTH,
    inputType: 'text'
  }
];

const INSTAGRAM_STORY_TEMPLATE: DeliverableDetailsFormTemplate = [
  {
    name: 'hashtags',
    label: __t('deliverable_details_form.hashtags_label'),
    tooltipMessage: __t('deliverable_details_form.hashtags_tooltip'),
    prefix: '#',
    max: MAX_ITEMS,
    inputType: 'array'
  },
  {
    name: 'mentions',
    label: __t('deliverable_details_form.mentions_label'),
    tooltipMessage: __t('deliverable_details_form.mentions_tooltip'),
    prefix: '@',
    max: MAX_ITEMS,
    inputType: 'array'
  },
  {
    name: 'location',
    label: __t('deliverable_details_form.location_label'),
    tooltipMessage: __t('deliverable_details_form.location_tooltip'),
    max: MAX_LOCATION_LENGTH,
    inputType: 'text'
  }
];

const INSTAGRAM_REEL_TEMPLATE: DeliverableDetailsFormTemplate = [
  {
    name: 'hashtags',
    label: __t('deliverable_details_form_v2.hashtags_label'),
    tooltipMessage: __t('deliverable_details_form_v2.hashtags_tooltip'),
    prefix: '#',
    max: MAX_ITEMS,
    inputType: 'array'
  },
  {
    name: 'tags',
    label: __t('deliverable_details_form_v2.tags_label'),
    tooltipMessage: __t('deliverable_details_form_v2.tags_tooltip_instagram'),
    prefix: '@',
    max: MAX_ITEMS,
    inputType: 'array'
  },
  {
    name: 'mentions',
    label: __t('deliverable_details_form_v2.mentions_label'),
    tooltipMessage: __t('deliverable_details_form_v2.mentions_tooltip'),
    prefix: '@',
    max: MAX_ITEMS,
    inputType: 'array'
  }
];

const YOUTUBE_VIDEO_TEMPLATE: DeliverableDetailsFormTemplate = [
  {
    name: 'tags',
    label: __t('deliverable_details_form.tags_label'),
    tooltipMessage: __t('deliverable_details_form.tags_tooltip_youtube'),
    prefix: '',
    max: MAX_ITEMS,
    inputType: 'array'
  }
];

const TIKTOK_VIDEO_TEMPLATE: DeliverableDetailsFormTemplate = [
  {
    name: 'hashtags',
    label: __t('deliverable_details_form.hashtags_label'),
    tooltipMessage: __t('deliverable_details_form.hashtags_tooltip'),
    prefix: '#',
    max: MAX_ITEMS,
    inputType: 'array'
  },
  {
    name: 'mentions',
    label: __t('deliverable_details_form.mentions_label'),
    tooltipMessage: __t('deliverable_details_form.mentions_tooltip'),
    prefix: '@',
    max: MAX_ITEMS,
    inputType: 'array'
  }
];

export const INPUT_ROWS_TEMPLATES = {
  instagram_post: INSTAGRAM_POST_TEMPLATE,
  instagram_story: INSTAGRAM_STORY_TEMPLATE,
  instagram_reel: INSTAGRAM_REEL_TEMPLATE,
  instagram_regram_post: INSTAGRAM_POST_TEMPLATE,
  instagram_regram_story: INSTAGRAM_STORY_TEMPLATE,
  youtube_video: YOUTUBE_VIDEO_TEMPLATE,
  tiktok_video: TIKTOK_VIDEO_TEMPLATE,
  blog: [],
  other: [],
  content_production: []
};

const LANDING_PAGE_ITEMS = [
  { name: 'landingPageUrl' },
  { name: 'landingPageDescription' }
];

const LANDING_PAGE_TEMPLATE = {
  instagram_post: LANDING_PAGE_ITEMS,
  instagram_story: LANDING_PAGE_ITEMS,
  instagram_regram_post: LANDING_PAGE_ITEMS,
  instagram_regram_story: LANDING_PAGE_ITEMS,
  youtube_video: LANDING_PAGE_ITEMS,
  tiktok_video: LANDING_PAGE_ITEMS,
  blog: LANDING_PAGE_ITEMS,
  other: [],
  content_production: []
};

type Props = {
  deliverable: Deliverable,
  onEdit: (a: Partial<Deliverable>) => void
};

type State = {
  deliverable: Deliverable
};

class DeliverableDetailsForm extends React.PureComponent<Props, State> {
  throttledHandleEdit: () => void;

  constructor(props: Props) {
    super(props);

    const deliverable = props.deliverable;

    const trackingLinks = !isEmpty(deliverable.trackingLinks)
      ? deliverable.trackingLinks
      : [{ baseUrl: '', description: '' }];

    this.state = {
      deliverable: {
        ...deliverable,
        landingPageUrl: deliverable.landingPageUrl || trackingLinks[0].baseUrl,
        landingPageDescription: deliverable.landingPageDescription || trackingLinks[0].description || ''
      }
    };

    this.throttledHandleEdit = throttle(this.handleEdit, 1000);
  }

  handleTagChange = (tag: { tag: DeliverableTagAlias | undefined | null }): void => {
    this.setState(state => ({
      deliverable: {
        ...state.deliverable,
        ...tag
      }
    }), this.handleEdit);
  };

  handleDetailsArrayChanged = (name: string, items: Array<string>): void => {
    this.setState(state => ({
      deliverable: {
        ...state.deliverable,
        [name]: items
      }
    }), this.handleEdit);
  };

  handleDetailsInputChanged = (event: SyntheticEvent<TextEntry>): void => {
    const target = event.currentTarget;
    const name = target.name || '';
    const value = target.value || '';

    this.setState(state => ({
      deliverable: {
        ...state.deliverable,
        [String(name)]: value
      }
    }), this.throttledHandleEdit);
  };

  handleCopy = (event: SyntheticEvent<HTMLElement>) => {
    const target = event.currentTarget;

    copyTextToClipboard(`[data-copy-key="${target.dataset.keyToCopy}"]`);
  };

  handleEdit() {
    const { onEdit } = this.props;
    const { deliverable } = this.state;

    const fieldsToCheck = [
      ...INPUT_ROWS_TEMPLATES[deliverable.deliverableType] || [],
      ...LANDING_PAGE_TEMPLATE[deliverable.deliverableType] || []
    ].filter(isDefined);

    const updatedFieldNames = fieldsToCheck.map(item => item.name);
    const deliverablePartial: Partial<Deliverable> = pick(deliverable, updatedFieldNames);

    onEdit(deliverablePartial);
  }

  renderCopyControl(deliverable: Deliverable, item: DeliverableDetailsFormTemplate[0]): React.ReactNode {
    return (
      <Tooltip
        message={t('action_button_copy')}
        width="micro"
      >
        <div className="deliverable-details-edit__tags-copy">
          <Tooltip
            message={t('copied')}
            width="micro"
            trigger="click"
          >
            <SVGDynamicIcon
              height={16}
              width={16}
              name="duplicate-custom-fg"
              customColor="grey"
              data={{ 'key-to-copy': `${deliverable.id}-${item.name}-field-to-copy` }}
              onClick={this.handleCopy}
            />
          </Tooltip>
        </div>
      </Tooltip>
    );
  }

  renderDynamicFields(): React.ReactNode {
    const { deliverable } = this.state;

    return (INPUT_ROWS_TEMPLATES[deliverable.deliverableType] || []).map(item => (
      <div
        className="deliverable-details-edit__section"
        key={`deliverable-details-form-${item.name}`}
      >
        <FormField
          showValidationIcon={false}
          label={item.label}
          labelType="highlighted"
          tooltipMessage={item.tooltipMessage || ''}
          direction="column"
          fontSize="small"
          validators={
            item.inputType === 'text'
              ? [maxLength(deliverable[item.name], item.max)]
              : []
          }
        >
          {
            item.inputType === 'array' &&
              <div
                className="deliverable-details-edit__tags"
                data-copy-key={`${deliverable.id}-${item.name}-field-to-copy`}
              >
                <InputArray
                  name={item.name}
                  prefix={item.prefix}
                  value={deliverable[item.name] || []}
                  max={item.max}
                  onChange={(items) => this.handleDetailsArrayChanged(item.name, items)}
                />
                {this.renderCopyControl(deliverable, item)}
              </div>
          }
          {
            item.inputType === 'text' &&
              <div
                className="deliverable-details-edit__input"
                data-copy-key={`${deliverable.id}-${item.name}-field-to-copy`}
              >
                <Input
                  width="responsive"
                  name={item.name}
                  value={deliverable[item.name] || ''}
                  onChange={this.handleDetailsInputChanged}
                />
                {this.renderCopyControl(deliverable, item)}
              </div>
          }
        </FormField>
      </div>
    ));
  }

  renderLandingPageFields(): React.ReactNode {
    const { deliverable } = this.state;

    return (
      <div className="deliverable-details-edit__section">
        <DeliverableLandingPageFields
          deliverable={deliverable}
          onInputChanged={this.handleDetailsInputChanged}
        />
      </div>
    );
  }

  private renderTag(): React.ReactNode {
    return (
      <div className="deliverable-details-edit__section">
        <DeliverableTag
          deliverableTag={this.state.deliverable.tag ?? null}
          onEdit={this.handleTagChange}
        />
      </div>
    );
  }

  render(): React.ReactNode {
    return (
      <div className="deliverable-details-form">
        {this.renderDynamicFields()}
        {this.renderTag()}
        {this.renderLandingPageFields()}
      </div>
    );
  }
}

export default DeliverableDetailsForm;
